import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  Search,
  ShoppingCart,
  User,
  Menu,
  LogOut,
  Package,
} from "lucide-react"; // Import Lucide icons
import { Link, useLocation, useNavigate } from "react-router-dom";
import NavDrawer from "../Drawer/NavDrawer";
import MegaMenu from "src/components/ui-elements/MegaMenu";
import SearchDrawer from "../Drawer/SearchDrawer";
import { fetchCollections } from "src/services/CollectionApi";
import { useSelector } from "react-redux";
import { store } from "src/store/store";
import { clearAccessToken } from "src/store/slices/authSlice";
import { showToast } from "src/components/ToastProvider";
import ToastTypes from "src/enum/ToastTypes";
import { setnavigateCheckout } from "src/store/slices/navigateCheckout-slice";
import PageDropdownMenu from "src/layout/PageDropdownMenu";
import { useExpertHubNavigation } from "src/hooks/useExpertHubNavigation";
import { useTheme } from "src/contexts/ThemeContext";
import { getImageUrl } from "src/utils/imageUrl";

const Navbar = ({ toggleCartDrawer }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [navbarPages, setNavbarPages] = useState({});
  const { showShop, showCollections, showServices, loading: navLoading } = useExpertHubNavigation();
  const { themeConfig } = useTheme();
  const isInstant = themeConfig?.preset === 'instant';
  const faqConfig = themeConfig?.faq;
  const faqNavEnabled = !!(faqConfig?.enabled && faqConfig?.displayInNav);
  const globalData = useSelector((state) => state.globalData.globalDataName);
  const imageUrl = getImageUrl(globalData.logoUrl);
  const categories = useSelector((state) => state.category.categoryArray);
  const isTrending = useSelector((state) => state.productCategory.Trending);
  const isNewArrival = useSelector((state) => state.productCategory.NewArrival);
  const isBestSeller = useSelector((state) => state.productCategory.BestSeller);
  const accessToken = useSelector((state) => state.accessToken.token);
  const [activeMenu, setActiveMenu] = useState(null);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const storePages = useSelector((state) => state.storePages?.items);
  const blogs = useSelector((state) => state.blogs.items);
  const firstTenCategories = categories.slice(0, 9);
  const nextTenCategories = categories.slice(9, 17);
  const navigate = useNavigate();
  const row1Entries = [];
  const location = useLocation()

  if (isTrending) {
    row1Entries.push({ heading: "Trending" });
  }

  if (isNewArrival) {
    row1Entries.push({ heading: "New Arrivals" });
  }

  if (isBestSeller) {
    row1Entries.push({ heading: "Best Sellers" });
  }
  const row2 = firstTenCategories
    ? [
        {
          heading: firstTenCategories[0]?.category,
        },
        ...firstTenCategories.slice(1).map((cat) => ({ name: cat?.category })),
        { name: "Shop All" },
      ]
    : [];

  const row3 =
    nextTenCategories.length > 0
      ? [
          {
            heading: nextTenCategories[0]?.category,
          }, // First item as heading
          ...nextTenCategories.slice(1).map((cat) => ({ name: cat?.category })),
          { name: "Shop All" },
        ]
      : [];

  // Fetch collections from API
  useEffect(() => {
    const getCollections = async () => {
      const collectionsData = await fetchCollections();
      setCollections(collectionsData);
    };

    getCollections();
  }, []);

  // Filter and group pages that should be displayed in navbar
  useEffect(() => {
    if (storePages && storePages.length > 0) {
      const navbarDisplayPages = storePages.filter(page => 
        page.Display_Location && page.Display_Location.includes("navbar")
      );
      
      // Group pages by their Group field
      const groupedPages = navbarDisplayPages.reduce((acc, page) => {
        const groupKey = page.Group || null;
        if (groupKey) {
          // If has group, add to grouped section
          if (!acc[groupKey]) {
            acc[groupKey] = [];
          }
          acc[groupKey].push(page);
        } else {
          // If no group, add to ungrouped section
          if (!acc['ungrouped']) {
            acc['ungrouped'] = [];
          }
          acc['ungrouped'].push(page);
        }
        return acc;
      }, {});
      
      setNavbarPages(groupedPages);
    }
  }, [storePages]);

  const handleUserClick = () => {
    if (!accessToken) {
      console.log("Not logged in yet");
      navigate("/login");
    } else {
      console.log("User is logged in, show menu");
    }
  };

  const handleLogout = () => {
    store.dispatch(clearAccessToken()); // Clear token from Redux store
    store.dispatch(setnavigateCheckout(false)); // Clear checkout navigation flag
    showToast("Logged out successfully.", ToastTypes.SUCCESS);
    navigate("/login"); // Redirect to login after logout
  };

  // Create collection submenu card data from API
  const getCollectionSubmenuData = (collections) => {
    const filteredCollections = collections.filter((collection) => {
      const hasImage = !!collection.image;
      return hasImage;
    });

    return filteredCollections.map((collection) => ({
      heading: collection.name,
      img: collection.image,
      link: `/collection/${collection.slug}`,
    }));
  };

  // Create navbar menu items with dynamic pages (limited to 3 items)
  const createMenuItems = () => {
    const baseMenuItems = [
      // For Instant theme, show categories directly in navbar instead of Shop menu
      ...(isInstant && categories.length > 0
        ? categories.slice(0, 5).map((category) => ({
            name: category.category,
            link: `/category/${encodeURIComponent(category.category)}`,
          }))
        : []),
      // Only include Shop if it should be visible (and not Instant theme)
      ...(showShop && !isInstant
        ? [
            {
              name: "Shop",
              link: "/category/all",
              subMenu: true,
              subMenuGridData: {
                row1: row1Entries,
                row2,
                row3,
              },
            },
          ]
        : []),
      // Only include Collections if collections are available AND if it should be visible
      ...(collections.length > 0 && showCollections
        ? [
            {
              name: "Collections",
              link: "/",
              subMenu: true,
              subMenuCardData: getCollectionSubmenuData(collections),
              drawerSubMenu: collections
                .filter((collection) => collection.image)
                .map((collection) => ({
                  heading: collection.name,
                  link: `/collection/${collection.slug}`,
                })),
            },
          ]
        : []),
      // Only include Services if it should be visible (Expert Hub theme with type 2 products)
      ...(showServices
        ? [
            {
              name: "Services",
              link: "/services",
            },
          ]
        : []),
      // Seller-configured FAQ plugin shown as a direct link (no group set)
      ...(faqNavEnabled && !faqConfig?.navGroup
        ? [
            {
              name: "FAQs",
              link: "/faq",
            },
          ]
        : []),
    ];

    // Merge the FAQ plugin into the grouped-pages data when a nav group is configured,
    // so it rides the same dropdown mechanism as seller-created CMS pages.
    const effectiveNavbarPages = { ...navbarPages };
    if (faqNavEnabled && faqConfig?.navGroup) {
      const groupKey = faqConfig.navGroup;
      effectiveNavbarPages[groupKey] = [
        ...(effectiveNavbarPages[groupKey] || []),
        { id: "__faq__", title: "FAQs", slug: "faq", link: "/faq" },
      ];
    }

    // Add dynamic navbar pages with limit of 3
    const dynamicMenuItems = [];
    let itemCount = 0;
    const maxItems = 3;

    // Add ungrouped pages (direct links) first
    if (effectiveNavbarPages.ungrouped && itemCount < maxItems) {
      const remainingSlots = maxItems - itemCount;
      const ungroupedToAdd = effectiveNavbarPages.ungrouped.slice(0, remainingSlots);

      ungroupedToAdd.forEach(page => {
        dynamicMenuItems.push({
          name: page.title,
          link: page.link || `/page/${page.slug}`,
          isPage: true,
          pageData: page
        });
        itemCount++;
      });
    }

    // Add grouped pages (with dropdown using PageDropdownMenu)
    if (itemCount < maxItems) {
      const groupKeys = Object.keys(effectiveNavbarPages).filter(key => key !== 'ungrouped');

      for (const groupKey of groupKeys) {
        if (itemCount >= maxItems) break;

        const groupPages = effectiveNavbarPages[groupKey];
        dynamicMenuItems.push({
          name: groupKey,
          link: "/",
          subMenu: true,
          isPageGroup: true,
          subMenuPagesData: groupPages.map(page => ({
            heading: page.title,
            link: page.link || `/page/${page.slug}`,
            img: page.Image?.filename_disk || null,
            description: page.content,
            pageData: page
          })),
          subMenuPageData: groupPages.map(page => ({
            heading: page.title,
            pageData: page
          })),
          drawerSubMenu: groupPages.map(page => ({
            heading: page.title,
            link: page.link || `/page/${page.slug}`,
          }))
        });
        itemCount++;
      }
    }

    // Add Contact at the end
    const finalMenuItems = [
      ...baseMenuItems,
      ...dynamicMenuItems,
    ];

    // Only add Blogs if there are blogs available
    if (blogs && blogs.length > 0) {
      finalMenuItems.push({
        name: "Blogs",
        link: "/blogs",
      });
    }

    // Always add Contact at the end
    finalMenuItems.push({
      name: "Contact",
      link: "/contact",
    });

    return finalMenuItems;
  };


  const menuItems = createMenuItems();

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const toggleSearchDrawer = () => setIsSearchDrawerOpen(!isSearchDrawerOpen);
  const handleMouseEnter = (menuName) => setActiveMenu(menuName);
  const handleMouseLeave = () => setActiveMenu(null);

  return (
    <nav
      className={`sticky p-4 w-full z-[1039]`}
      style={{
        top: "0",
        backgroundColor: 'var(--navbar-bg)',
        color: 'var(--navbar-link-color)'
      }}
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="text-2xl font-bold">
          <Link to="/">
            <img
              src={imageUrl}
              alt="siteLogo"
              className="w-[130px] md:w-auto h-[60px] rounded-xl object-contain"
            />
          </Link>
        </div>
        {/* middle section  */}
        <ul className="hidden md:flex gap-x-2">
          {!navLoading && menuItems.map((item, index) => (
            <li
              onMouseEnter={() => handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
              key={index}
              className={`group px-3 py-2 ${item.isPageGroup ? 'relative' : ''}`} 
            >
              <Link
                to={item.link}
                className="relative transition duration-300 text-lg font-outfitMedium"
                style={{ color: 'var(--navbar-link-hover-color)' }}
                onMouseEnter={(e) => e.target.style.color = 'var(--navbar-link-color)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--navbar-link-hover-color)'}
              >
                {item.name}
                {item.subMenu && (
                  <ChevronDown
                    size={16}
                    className="ml-1 inline-block hover:rotate-180"
                  />
                )}
              </Link>

              {/* Show MegaMenu for Shop and Collections */}
              {item.subMenu && !item.isPageGroup && activeMenu === item.name && (
                <div className="absolute left-0 right-0 w-full">
                  <MegaMenu
                    menuItems={[item]}
                    closeMenu={() => setActiveMenu(null)}
                  />
                </div>
              )}

              {/* Show PageDropdownMenu for page groups */}
              {item.subMenu && item.isPageGroup && activeMenu === item.name && (
                 <div className="absolute top-full z-50">
                  <PageDropdownMenu
                    data={item.subMenuPagesData}
                    closeMenu={() => setActiveMenu(null)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Right Section: Icons */}
        <div className="flex items-center space-x-3 md:space-x-5">
          {/* search button  */}
          <button
            onClick={toggleSearchDrawer}
            className="relative text-xl transition duration-300"
            style={{ color: 'var(--navbar-link-hover-color)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
            title={"Search"}
          >
            <Search size={24} />
          </button>

          {/* cart button  */}
          {location.pathname !== "/checkout" && (
            <button
              onClick={toggleCartDrawer}
              className="relative text-xl transition duration-300"
              style={{ color: 'var(--navbar-link-hover-color)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
              title={"Cart"}
            >
              <ShoppingCart size={24} />
              <span className="absolute top-0 font-outfitLight -right-4 text-[11px] p-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center -translate-x-1/2 -translate-y-1/2">
                {cartItems.length}
              </span>
            </button>
          )}

          {!accessToken ? (
            <button
              onClick={handleUserClick}
              className="relative text-xl transition duration-300"
              style={{ color: 'var(--navbar-link-hover-color)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
              title={"Login"}
            >
              <User size={24} />
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate("/orders")}
                className="relative text-xl transition duration-300"
                style={{ color: 'var(--navbar-link-hover-color)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                title={"My Orders"}
              >
                <Package size={24} />
              </button>

              <button
                onClick={handleLogout}
                className="relative text-xl transition duration-300"
                style={{ color: 'var(--navbar-link-hover-color)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                title={"Logout"}
              >
                <LogOut size={24} />
              </button>
            </>
          )}
          {/* Drawer Menu button  */}
          <button
            className="block md:hidden text-xl transition duration-300"
            style={{ color: 'var(--navbar-link-hover-color)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
            onClick={toggleDrawer}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Nav Drawer */}
      <NavDrawer
        isOpen={isDrawerOpen}
        onClose={toggleDrawer}
        menuItems={menuItems}
      />

      {/* search Drawer  */}
      <SearchDrawer isOpen={isSearchDrawerOpen} onClose={toggleSearchDrawer} />
    </nav>
  );
};

export default Navbar;
